import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Save, Trash2 } from 'lucide-react'
import type { SpreadAlert } from '@/types'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { TagExpressionInput } from '@/components/common/TagExpressionInput'
import { legacyTagsToExpression } from '@/lib/tagExpression'
import { TelegramDestinationSelect } from '@/components/settings/TelegramDestinationSelect'

interface SpreadAlertEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  alert?: SpreadAlert | null
  onSave: (alert: Partial<SpreadAlert>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

export function SpreadAlertEditSheet({
  open,
  onOpenChange,
  alert,
  onSave,
  onDelete,
}: SpreadAlertEditSheetProps) {
  const isNew = !alert?.id
  const exchangeOptions = useExchangeOptions('futures_ticker')

  const [name, setName] = useState('')
  const [exchanges, setExchanges] = useState<string[]>([])
  const [minDiffPct, setMinDiffPct] = useState([5])
  const [spreadUpdatePct, setSpreadUpdatePct] = useState([10])
  const [groupTolerancePct, setGroupTolerancePct] = useState([5])
  const [cooldownSeconds, setCooldownSeconds] = useState([60])
  const [enabled, setEnabled] = useState(true)
  const [enableTelegram, setEnableTelegram] = useState(true)
  const [enablePushover, setEnablePushover] = useState(false)
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramReplyTo, setTelegramReplyTo] = useState('')
  const [tagExpression, setTagExpression] = useState('')
  const [legacyRequired, setLegacyRequired] = useState<string[]>([])
  const [legacyExcluded, setLegacyExcluded] = useState<string[]>([])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (alert) {
      setName(alert.name || '')
      setExchanges(alert.exchanges || [])
      setMinDiffPct([alert.filters?.min_diff_pct || alert.min_diff_pct || 5])
      setSpreadUpdatePct([alert.filters?.spread_update_pct || alert.spread_update_pct || 10])
      setGroupTolerancePct([alert.filters?.spread_group_tolerance_pct || alert.spread_group_tolerance_pct || 5])
      setCooldownSeconds([alert.cooldown_seconds || 60])
      setEnabled(alert.enabled !== false)
      setEnableTelegram(alert.enable_telegram !== false)
      setEnablePushover(alert.enable_pushover === true)
      setTelegramChatId(alert.telegram?.chat_id || '')
      setTelegramReplyTo(String(alert.telegram?.reply_to_message_id ?? ''))
      const existingExpression = alert.tag_expression || ''
      if (existingExpression) {
        setTagExpression(existingExpression)
        setLegacyRequired([])
        setLegacyExcluded([])
      } else {
        const required = alert.required_tags || []
        const excluded = alert.exclude_tags || []
        setLegacyRequired(required)
        setLegacyExcluded(excluded)
        if (required.length > 0 || excluded.length > 0) {
          setTagExpression(legacyTagsToExpression(required, [], excluded))
        } else {
          setTagExpression('')
        }
      }
    } else {
      setName('')
      setExchanges([])
      setMinDiffPct([5])
      setSpreadUpdatePct([10])
      setGroupTolerancePct([5])
      setCooldownSeconds([60])
      setEnabled(true)
      setEnableTelegram(true)
      setEnablePushover(false)
      setTelegramChatId('')
      setTelegramReplyTo('')
      setTagExpression('')
      setLegacyRequired([])
      setLegacyExcluded([])
    }
  }, [alert, open])

  const toggleExchange = (exchange: string) => {
    setExchanges((prev) =>
      prev.includes(exchange)
        ? prev.filter((e) => e !== exchange)
        : [...prev, exchange]
    )
  }

  const handleSave = async () => {
    if (!name.trim() || exchanges.length < 2) return

    setIsSaving(true)
    try {
      const alertData: Partial<SpreadAlert> & { tag_expression?: string; alert_type?: string } = {
        ...(alert?.id && { id: alert.id }),
        name: name.trim(),
        alert_type: 'spread',
        exchanges,
        enabled,
        filters: {
          min_diff_pct: minDiffPct[0],
          spread_update_pct: spreadUpdatePct[0],
          spread_group_tolerance_pct: groupTolerancePct[0],
        },
        cooldown_seconds: cooldownSeconds[0],
        enable_telegram: enableTelegram,
        enable_pushover: enablePushover,
        required_tags: [],
        exclude_tags: [],
        tag_expression: tagExpression,
      }

      if (enableTelegram && (telegramChatId || telegramReplyTo)) {
        alertData.telegram = {
          chat_id: telegramChatId,
          reply_to_message_id: telegramReplyTo || undefined,
        }
      }
      await onSave(alertData)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save alert:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!alert?.id || !onDelete) return
    if (!confirm('Are you sure you want to delete this alert?')) return

    setIsDeleting(true)
    try {
      await onDelete(alert.id)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete alert:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Create Spread Alert' : 'Edit Spread Alert'}</SheetTitle>
          <SheetDescription>
            Track cross-exchange price spreads with live-updating Telegram messages.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="spread-alert-name">Alert Name</Label>
              <Input
                id="spread-alert-name"
                placeholder="Spread Alert"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="spread-alert-enabled">Enabled</Label>
              <Switch
                id="spread-alert-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Exchanges to Compare</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Select at least 2 exchanges to compare spreads
            </p>
            <div className="flex flex-wrap gap-2">
              {exchangeOptions.map((exchange) => (
                <Badge
                  key={exchange}
                  variant={exchanges.includes(exchange) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleExchange(exchange)}
                >
                  {exchange}
                </Badge>
              ))}
            </div>
            {exchanges.length < 2 && (
              <p className="text-xs text-destructive">Select at least two exchanges</p>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Min Spread Threshold</Label>
              <span className="text-sm font-medium">{minDiffPct[0]}%</span>
            </div>
            <Slider
              value={minDiffPct}
              onValueChange={setMinDiffPct}
              min={0.5}
              max={50}
              step={0.5}
            />
            <p className="text-xs text-muted-foreground">
              Alert when the spread between cheapest and most expensive exceeds this %
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Significant Change Threshold</Label>
              <span className="text-sm font-medium">{spreadUpdatePct[0]}%</span>
            </div>
            <Slider
              value={spreadUpdatePct}
              onValueChange={setSpreadUpdatePct}
              min={1}
              max={50}
              step={1}
            />
            <p className="text-xs text-muted-foreground">
              Send a new notification when spread changes by this % of previous value
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Grouping Tolerance</Label>
              <span className="text-sm font-medium">{groupTolerancePct[0]}%</span>
            </div>
            <Slider
              value={groupTolerancePct}
              onValueChange={setGroupTolerancePct}
              min={1}
              max={20}
              step={1}
            />
            <p className="text-xs text-muted-foreground">
              Exchanges within this % of each other are grouped together
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Cooldown</Label>
              <span className="text-sm font-medium">{cooldownSeconds[0]}s</span>
            </div>
            <Slider
              value={cooldownSeconds}
              onValueChange={setCooldownSeconds}
              min={10}
              max={600}
              step={10}
            />
          </div>

          <Separator />

          {/* Notifications */}
          <div className="space-y-4">
            <Label className="text-base">Notifications</Label>

            <div className="flex items-center justify-between">
              <Label htmlFor="spread-enable-telegram">Telegram</Label>
              <Switch
                id="spread-enable-telegram"
                checked={enableTelegram}
                onCheckedChange={setEnableTelegram}
              />
            </div>

            {enableTelegram && (
              <div className="pl-4">
                <div className="space-y-2">
                  <Label htmlFor="spread-telegram-destination">Destination</Label>
                  <TelegramDestinationSelect
                    id="spread-telegram-destination"
                    chatId={telegramChatId}
                    replyToMessageId={telegramReplyTo}
                    onChange={({ chat_id, reply_to_message_id }) => {
                      setTelegramChatId(chat_id)
                      setTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="spread-enable-pushover">Pushover</Label>
              <Switch
                id="spread-enable-pushover"
                checked={enablePushover}
                onCheckedChange={setEnablePushover}
              />
            </div>
          </div>

          <Separator />

          {/* Tags */}
          <div className="space-y-4">
            <TagExpressionInput
              value={tagExpression}
              onChange={setTagExpression}
              legacyRequired={legacyRequired}
              legacyExcluded={legacyExcluded}
            />
          </div>
        </div>

        <SheetFooter className="gap-2 sm:gap-0">
          {!isNew && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isDeleting || !name.trim() || exchanges.length < 2}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
